const DB_NAME = "wayf";
const DB_VERSION = 1;

interface StoredIdentity {
	uuid: string;
	nickname: string;
}

interface Friend {
	uuid: string;
	nickname: string;
}

interface GroupMember {
	uuid: string;
	nickname: string;
}

interface Group {
	uuid: string;
	name: string;
	members: GroupMember[];
}

export interface UserData {
	identity: StoredIdentity | null;
	privateKey: CryptoKey | null;
	friends: Friend[];
	groups: Group[];
	activeGroupUuids: string[];
}

function openDatabase(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve(request.result);

		request.onupgradeneeded = (event) => {
			const db = (event.target as IDBOpenDBRequest).result;

			if (!db.objectStoreNames.contains("identity")) {
				db.createObjectStore("identity", { keyPath: "key" });
			}
			if (!db.objectStoreNames.contains("privateKey")) {
				db.createObjectStore("privateKey", { keyPath: "key" });
			}
			if (!db.objectStoreNames.contains("friends")) {
				db.createObjectStore("friends", { keyPath: "uuid" });
			}
			if (!db.objectStoreNames.contains("groups")) {
				db.createObjectStore("groups", { keyPath: "uuid" });
			}
			if (!db.objectStoreNames.contains("settings")) {
				db.createObjectStore("settings", { keyPath: "key" });
			}
		};
	});
}

export async function getIdentity(): Promise<StoredIdentity | null> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("identity", "readonly");
		const store = tx.objectStore("identity");
		const request = store.get("current");

		request.onerror = () => reject(request.error);
		request.onsuccess = () => {
			const result = request.result;
			resolve(result ? { uuid: result.uuid, nickname: result.nickname } : null);
		};
	});
}

export async function setIdentity(identity: StoredIdentity): Promise<void> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("identity", "readwrite");
		const store = tx.objectStore("identity");
		const request = store.put({ key: "current", ...identity });

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve();
	});
}

export async function getPrivateKey(): Promise<CryptoKey | null> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("privateKey", "readonly");
		const store = tx.objectStore("privateKey");
		const request = store.get("current");

		request.onerror = () => reject(request.error);
		request.onsuccess = () => {
			const result = request.result;
			resolve(result ? result.privateKey : null);
		};
	});
}

export async function setPrivateKey(privateKey: CryptoKey): Promise<void> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("privateKey", "readwrite");
		const store = tx.objectStore("privateKey");
		const request = store.put({ key: "current", privateKey });

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve();
	});
}

export async function getFriends(): Promise<Friend[]> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("friends", "readonly");
		const store = tx.objectStore("friends");
		const request = store.getAll();

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve(request.result || []);
	});
}

export async function addFriend(friend: Friend): Promise<void> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("friends", "readwrite");
		const store = tx.objectStore("friends");
		const request = store.put(friend);

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve();
	});
}

export async function removeFriend(uuid: string): Promise<void> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("friends", "readwrite");
		const store = tx.objectStore("friends");
		const request = store.delete(uuid);

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve();
	});
}

export async function getGroups(): Promise<Group[]> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("groups", "readonly");
		const store = tx.objectStore("groups");
		const request = store.getAll();

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve(request.result || []);
	});
}

export async function getGroup(uuid: string): Promise<Group | null> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("groups", "readonly");
		const store = tx.objectStore("groups");
		const request = store.get(uuid);

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve(request.result || null);
	});
}

export async function saveGroup(group: Group): Promise<void> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("groups", "readwrite");
		const store = tx.objectStore("groups");
		const request = store.put(group);

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve();
	});
}

export async function removeGroup(uuid: string): Promise<void> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("groups", "readwrite");
		const store = tx.objectStore("groups");
		const request = store.delete(uuid);

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve();
	});
}

export async function getActiveGroupUuids(): Promise<string[]> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("settings", "readonly");
		const store = tx.objectStore("settings");
		const request = store.get("activeGroupUuids");

		request.onerror = () => reject(request.error);
		request.onsuccess = () => {
			const result = request.result;
			resolve(result ? result.value : []);
		};
	});
}

export async function setActiveGroupUuids(uuids: string[]): Promise<void> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction("settings", "readwrite");
		const store = tx.objectStore("settings");
		const request = store.put({ key: "activeGroupUuids", value: uuids });

		request.onerror = () => reject(request.error);
		request.onsuccess = () => resolve();
	});
}

export async function clearAllData(): Promise<void> {
	const db = await openDatabase();
	return new Promise((resolve, reject) => {
		const stores = ["identity", "privateKey", "friends", "groups", "settings"];
		const tx = db.transaction(stores, "readwrite");

		tx.onerror = () => reject(tx.error);
		tx.oncomplete = () => resolve();

		for (const storeName of stores) {
			tx.objectStore(storeName).clear();
		}
	});
}
